import React, { useCallback, useEffect, useState } from 'react';
import { RoiCell, DaySubtotalRow } from './RoiCell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const etDate = (offset = 0) => {
  const d = new Date(Date.now() + offset * 86400000);
  return d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
};

const clamp = (d, lo, hi) => (lo && d < lo ? lo : hi && d > hi ? hi : d);

const ResultCell = ({ r, i, onDelete }) => {
  const res = r.result || {};
  const cls = res.win ? 'text-orange-400' : res.blocked ? 'text-rose-300' : res.held || res.external ? 'text-amber-400' : res.ok ? 'text-emerald-400' : 'text-rose-400';
  const txt = res.simulated ? `◌ SIM — ${res.error || 'FS disabled'}`
    : res.blocked ? `⛔ BLOCKED — ${res.reason || 'Track not supported by ASP'}`
    : res.held ? `⏸ HELD — ${res.reason}`
    : res.external ? '✓ External'
    : res.ok ? `✓ ${res.status_code ?? ''}` : `⚠ ${res.error || res.status_code || 'error'}`;
  return (
    <td className="px-2 py-1.5">
      <span className={cls} title={res.reason || undefined} data-testid={`autotri-audit-result-${i}`}>{txt}</span>
      {onDelete && res.external && r.source_file && (
        <button onClick={() => onDelete(r)} title="Delete this external row"
          className="ml-2 px-1.5 rounded text-rose-400 hover:bg-rose-500/20 transition-colors"
          data-testid={`autotri-audit-delete-${i}`}>✕</button>
      )}
    </td>
  );
};

const LogRow = ({ r, i, onDelete }) => (
  <tr className={`border-t border-slate-800 ${r.result?.win ? 'text-orange-400' : r.result?.external ? 'text-amber-400' : 'text-slate-300'}`} data-testid={`autotri-audit-row-${i}`}>
    <td className="px-2 py-1.5 whitespace-nowrap">{r.ts ? new Date(r.ts).toLocaleString('en-US', { timeZone: 'America/New_York' }) : '—'}</td>
    <td className="px-2 py-1.5 whitespace-nowrap">{r.track} R{r.race}</td>
    <td className="px-2 py-1.5">{r.fs}</td>
    <td className="px-2 py-1.5">{r.variant === 'win' ? `Win ${r.version || 'C'}` : r.variant === 'manual' ? 'External' : r.variant === 'nostrong' ? 'Keys-Only' : r.variant === 'strong8' ? 'Strong FS8' : 'Strong'}</td>
    <td className="px-2 py-1.5">{r.version}</td>
    <td className="px-2 py-1.5">{r.combos}</td>
    <ResultCell r={r} i={i} onDelete={onDelete} />
    <RoiCell grade={r.grade} whatIf={r.result?.held || r.result?.blocked || r.result?.simulated} testId={`autotri-audit-roi-${i}`} />
  </tr>
);

/** Auto Tri "Sent to ASP" audit log with From/To + Apply. Used by Admin (Jobs) and the TriBet Dashboard. */
export const AutoTriLogPanel = ({ endpoint, minDate, maxDate, deletable = false }) => {
  const hi = maxDate || etDate(0);
  const [from, setFrom] = useState(clamp(etDate(-1), minDate, hi));
  const [to, setTo] = useState(clamp(etDate(0), minDate, hi));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async (s, e) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}${endpoint}?start=${s}&end=${e}`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setRows(d.recent || []); }
    } catch (err) { console.error('autotri-log', err); }
    finally { setLoading(false); }
  }, [endpoint]);
  useEffect(() => { load(from, to); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps
  const onDelete = deletable ? async (r) => {
    if (!window.confirm(`Delete external bet ${r.track} R${r.race} (${r.date})?\n${r.source_file}\n\nThis removes it from the log and all totals.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/feed/autotri-log/external?source_file=${encodeURIComponent(r.source_file)}`,
        { method: 'DELETE', credentials: 'include' });
      if (res.ok) setRows((rs) => rs.filter((x) => x.source_file !== r.source_file));
      else window.alert('Delete failed');
    } catch (err) { console.error('autotri-delete', err); }
  } : null;
  const dates = [...new Set(rows.map((r) => r.date))];
  const days = dates.length;
  const real = rows.filter((r) => !r.result?.simulated);
  const [openDays, setOpenDays] = useState({});
  return (
    <div className="mt-3" data-testid="autotri-log-panel">
      <div className="flex flex-wrap items-center justify-end gap-2 text-xs text-slate-400 mb-2" data-testid="autotri-log-range">
        <span>From</span>
        <input type="date" value={from} min={minDate || undefined} max={to} onChange={(e) => setFrom(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-slate-200" data-testid="autotri-log-from" />
        <span>To</span>
        <input type="date" value={to} min={from} max={hi} onChange={(e) => setTo(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-slate-200" data-testid="autotri-log-to" />
        <button onClick={() => load(clamp(from, minDate, hi), clamp(to, minDate, hi))} disabled={loading}
          className="px-3 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-semibold transition-colors"
          data-testid="autotri-log-apply">{loading ? 'Loading…' : 'Apply'}</button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-800" data-testid="autotri-audit-log">
        <table className="w-full text-xs">
          <thead className="bg-slate-800/60 text-slate-400">
            <tr>
              {['When (ET)', 'Track/Race', 'FS', 'Type', 'Version', 'Combos', 'Result'].map((h) => <th key={h} className="text-left px-2 py-1.5">{h}</th>)}
              <th className="text-right px-2 py-1.5">ROI</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-2 py-3 text-center text-slate-600">No auto-tri fires in this date range.</td></tr>
            )}
            {days > 1 ? dates.map((d) => (
              <React.Fragment key={d}>
                <DaySubtotalRow date={d} rows={real.filter((x) => x.date === d)} colSpan={7} label={`${d} total`}
                  testId={`autotri-day-subtotal-${d}`} open={!!openDays[d]}
                  onToggle={() => setOpenDays((o) => ({ ...o, [d]: !o[d] }))} />
                {openDays[d] && rows.map((r, i) => (r.date === d ? <LogRow key={i} r={r} i={i} onDelete={onDelete} /> : null))}
              </React.Fragment>
            )) : rows.map((r, i) => <LogRow key={i} r={r} i={i} onDelete={onDelete} />)}
            {rows.length > 0 && (() => {
              const ext = real.filter((x) => x.result?.external);
              const win = real.filter((x) => x.result?.win);
              const auto = real.filter((x) => !x.result?.external && !x.result?.win);
              const span = `${days} day${days === 1 ? '' : 's'}`;
              return (<>
                {auto.length > 0 && <DaySubtotalRow rows={auto} colSpan={7} testId="autotri-grand-total-auto" label={`Grand total — Auto (${span})`} />}
                {ext.length > 0 && <DaySubtotalRow rows={ext} colSpan={7} testId="autotri-grand-total-external" label={`Grand total — External (${span})`} />}
                {win.length > 0 && <DaySubtotalRow rows={win} colSpan={7} testId="autotri-grand-total-win" label={`Grand total — Win (${span})`} />}
              </>);
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
};
